import { Button } from '@cfa/react-components';
import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import close from '../../../components/Icon/icons/close.svg';
import open from '../../../components/Icon/icons/open.svg';
import QuantityStepper from '../../../components/QuantityStepper';
import ooeConstants from '../../../constants';
import { formatPrice } from '../../../util/format';
import { ensureValueIsWholeNumber } from '../../../util/validate';
import { actions as cartActions } from '../../reducer';
import { selectShowMaxPromoFreeItemCountWarning } from '../../selectors';
import EditCartItem from '../EditCartItem';

interface Props {
  editMode?: boolean;
  item: CartItemView;
}

export const CartItem: React.FC<Props> = ({ editMode = false, item }) => {
  const { tag, quantity, name, price, id, promoFree } = item;

  const dispatch = useDispatch();
  const showPromoFreeMaxQtyWarning = useSelector(selectShowMaxPromoFreeItemCountWarning);

  const [stepperValue, setStepperValue] = useState(quantity);

  const [isEditMode, setIsEditMode] = useState(editMode);

  const maxQty = promoFree && showPromoFreeMaxQtyWarning ? quantity : 999;

  useEffect(() => {
    setIsEditMode(editMode);
  }, []);

  const handleToggleEditMode = () => {
    setIsEditMode((prev) => !prev);
  };

  const onQuantityChange = (newQuantity: number) => {
    setStepperValue(newQuantity);
    const qty = ensureValueIsWholeNumber(newQuantity.toString());
    if (qty < quantity) {
      dispatch(cartActions.removeNontoggleableModifiers({ id }));
    }
    dispatch(cartActions.updateQuantity({ id, quantity: qty }));
  };

  return (
    <StyledCartItem>
      <div className={isEditMode ? 'edit-mode' : ''}>
        <div className="item-box" key={tag}>
          <QuantityStepper
            className="quantity-stepper"
            data-cy={`quantity-${tag}`}
            max={maxQty}
            min={0}
            onQuantityChange={onQuantityChange}
            quantity={stepperValue}
            useTrashCan
          />
          <div className="toggleable-item" onClick={handleToggleEditMode}>
            {!promoFree && <div className="max-warning" />}
            {promoFree && showPromoFreeMaxQtyWarning && <div className="max-promo-free-warning" />}
            <div className="name" data-cy={`name-${tag}`}>
              {name}
            </div>
            <div data-cy={`${item.tag}-price`}>{formatPrice(price)}</div>
            <div className="promo-free-container" data-cy={`${item.tag}-promofree`}>
              {promoFree && (
                <div className="promo-free-indicator" data-testid="promo-free-indicator">
                  Promo Free
                </div>
              )}
            </div>
            <div>
              <Button
                className="edit-button"
                data-cy={`edit-${tag}`}
                data-testid="edit-button"
                variant="text"
              >
                {isEditMode ? <img alt="Close Edit" src={close} /> : <img alt="Open Edit" src={open} />}
              </Button>
            </div>
          </div>
        </div>
        {isEditMode && <EditCartItem cartItem={item} toggleEditMode={handleToggleEditMode} />}
      </div>
    </StyledCartItem>
  );
};

const StyledCartItem = styled.div`
  border-bottom: 2px solid ${(props) => props.theme.colors.disabled};
  padding: 20px 0 15px;
  font: ${(props) => props.theme.regularTextFont};

  & .item-box {
    display: flex;
    position: relative;

    & .toggleable-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-left: 20px;
      margin-bottom: 10px;
      width: 100%;
    }
  }

  & .quantity-stepper {
    align-self: flex-start;
  }

  & .name {
    min-width: 50%;
    margin-right: 20px;
  }

  & .promo-free-container {
    min-width: 20%;
  }

  & .max-warning,
  & .max-promo-free-warning {
    position: absolute;
    bottom: -17px;
    left: 0;
  }

  input:invalid + .max-warning::after,
  & .max-promo-free-warning::after {
    content: '999 max';
    font: ${(props) => props.theme.smallBoldFont};
    color: ${(props) => props.theme.colors.error};
    padding-left: 2px;
  }

  & .max-promo-free-warning::after {
    content: 'Total QTY of promo free items must be less than ${ooeConstants.MAX_PROMO_FREE_ITEM_QTY}';
  }

  & .edit-button {
    height: 39px;
    width: 39px;
    min-width: unset;

    :focus {
      outline: none;
    }
  }

  & .edit-button img {
    width: 20px;
  }

  & .special-instructions {
    padding: 7px 10px;
    margin-bottom: 40px;
    max-width: unset;
  }

  & .click-wrapper:hover {
    color: ${(props) => props.theme.colors.accent};
    cursor: pointer;
    font: ${(props) => props.theme.regularBoldFont};
  }
`;

export default memo(CartItem);
